import { Fragment } from 'react';
import { differenceInMinutes, format, formatDistanceToNow } from 'date-fns';
import { Avatar, Box, Divider, Group, Paper, Stack, Text } from '@mantine/core';
import { useUserContext } from '@state/userContext';
import { UserRole } from '@constants/userRoles';
import defaultAvatar from '@assets/default-avatar.png';
import IMessageData, {
  CombinedMessage,
  ISystemMessageCombined,
  SystemMessageType,
} from '@th-types/message.type';
import DATE_FORMATS from '@constants/dateFormat';
import { UseMutationResult } from '@tanstack/react-query';
import ChatMessage from '@components/Chat/ChatBody/ChatMessage/ChatMessage';
import { ChatMemberDisplay } from '@th-types/chat.type';

interface ChatBodyProps {
  chatMessages: CombinedMessage[];
  chatMembers: ChatMemberDisplay[];
  resendMessageMutation: UseMutationResult<
    { success: boolean },
    Error,
    { message: string; messageId: number },
    unknown
  >;
  /** When set, renders an Unread divider immediately before this message */
  firstUnreadMessageId?: number | null;
}

function ChatBody({
  chatMessages,
  chatMembers,
  resendMessageMutation,
  firstUnreadMessageId = null,
}: ChatBodyProps) {
  const { id: userId, name, profileImageUrl, hasRole } = useUserContext();

  const getAuthorName = (authorId: number | undefined): string => {
    if (authorId === userId) {
      return name;
    }
    const author = chatMembers?.find((person) => person.id === authorId);
    return author ? author.fullName : '';
  };

  const getAvatarImageUrl = (authorId: number | undefined): string => {
    if (authorId === userId) {
      return profileImageUrl;
    }
    const author = chatMembers?.find((member) => member.id === authorId);
    return author ? author.profileImageUrl : '';
  };

  const isFirstMessageInBlock = (
    index: number,
    messages: CombinedMessage[]
  ) => {
    if (index === 0) {
      return true;
    }

    const actualMessage = messages[index];
    const previousMessage = messages[index - 1];
    const actualDate = new Date(actualMessage?.created as Date);
    const previousDate = new Date(previousMessage?.created as Date);

    return (
      (actualMessage?.type === 'message' &&
        previousMessage?.type === 'message' &&
        (actualMessage.authorId !== previousMessage.authorId ||
          (actualDate.getTime() - previousDate.getTime()) / 1000 / 60 > 1)) ||
      (actualMessage?.type === 'message' && previousMessage?.type === 'system')
    );
  };

  const getTimeDisplay = (date: Date): string => {
    const minutesAgo = differenceInMinutes(new Date(), date);
    // Show relative time only if within the last 60 minutes
    if (minutesAgo < 60) {
      return formatDistanceToNow(date, { addSuffix: true });
    }
    return format(date, DATE_FORMATS.DATE_TIME_FORMAT);
  };

  const renderSystemMessage = (systemMessage: ISystemMessageCombined) => {
    const date = new Date(systemMessage.created);
    const formattedDate = format(date, DATE_FORMATS.DATE_TIME_FORMAT);

    const hasApplicationNote =
      systemMessage.systemType === SystemMessageType.WORKER_INTERESTED_IN_JOB &&
      systemMessage.text;

    if (hasApplicationNote) {
      const timeDisplay = getTimeDisplay(date);
      // Workers have ROLE_USER but not ROLE_COMPANY - show their application on the right
      const isMyApplication =
        hasRole(UserRole.ROLE_USER) && !hasRole(UserRole.ROLE_COMPANY);
      // Use logged-in user's avatar if it's their application, otherwise find worker in chatMembers
      const workerAvatar = isMyApplication
        ? profileImageUrl || defaultAvatar
        : chatMembers?.find((m) => m.type === 'Worker')?.profileImageUrl ||
          defaultAvatar;

      return (
        <Stack gap="xs" w="100%">
          <Text size="xs" fs="italic" ta="center">
            {formattedDate} {systemMessage.eventText}
          </Text>
          <Group
            gap="sm"
            align="flex-start"
            wrap="nowrap"
            w="100%"
            style={{
              flexDirection: isMyApplication ? 'row-reverse' : undefined,
            }}
          >
            <Avatar
              src={workerAvatar}
              alt="worker-avatar"
              size={41}
              radius={4}
            />
            <Stack
              gap={4}
              align={isMyApplication ? 'flex-end' : 'flex-start'}
              w="100%"
              maw={500}
            >
              <Paper
                pos="relative"
                radius="md"
                pt="xl"
                px="md"
                pb="md"
                w="100%"
                bd="1px solid var(--yellow)"
                style={{ boxShadow: 'none', overflow: 'hidden' }}
              >
                <Text
                  size="xs"
                  fw={600}
                  bg="var(--light-yellow)"
                  px={8}
                  py={4}
                  pos="absolute"
                  top={0}
                  left={0}
                  w="100%"
                  style={{ borderTopLeftRadius: 'var(--mantine-radius-md)' }}
                >
                  Application note
                </Text>
                <Text size="sm">{systemMessage.text}</Text>
              </Paper>
              <Text size="xs" c="dimmed" fs="italic">
                {timeDisplay}
              </Text>
            </Stack>
          </Group>
        </Stack>
      );
    }

    return (
      <Text size="xs" fs="italic" ta="center">
        {formattedDate} {systemMessage.eventText}
      </Text>
    );
  };

  const handleResendMessage = async (message: IMessageData) => {
    if (!message.sent && !message.sending) {
      await resendMessageMutation.mutateAsync({
        messageId: message.id,
        message: message.text,
      });
    }
  };

  return (
    <Stack py="md" gap="xs">
      {chatMessages.map((message, index) => {
        if (message.type === 'system') {
          return (
            <Fragment key={`system-${message.id}`}>
              {renderSystemMessage(message)}
            </Fragment>
          );
        }

        return (
          <Fragment key={message.id}>
            {firstUnreadMessageId != null &&
              message.id === firstUnreadMessageId && (
                <Box data-unread-divider my="sm">
                  <Divider label="Unread" labelPosition="center" />
                </Box>
              )}
            <ChatMessage
              message={message}
              authorName={getAuthorName(message.authorId)}
              avatarImageUrl={getAvatarImageUrl(message.authorId)}
              sentByMe={userId === message.authorId}
              resendMessage={() => handleResendMessage(message)}
              firstInBlock={isFirstMessageInBlock(index, chatMessages)}
            />
          </Fragment>
        );
      })}
    </Stack>
  );
}

export default ChatBody;
